import { navigate } from "@reach/router";
import React, { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useTheme } from "../../../context/ThemeContext";
import InfoIcon from "../../../images/icons/info-outline.inline.svg";
import { processForm } from "../../../utils/processForm";
import { bwMahlahlaProgramsTerm } from "../../../utils/sharedDatasets";
import CtaButton from "../../shared/buttons/ctaButton";
import { FormInputSelect } from "../../shared/form/FormInputSelect";
import ProgramCapacity from "../../shared/programCapacity";
import ProgramRating from "../../shared/programRating";
import { StatusComp } from "../../shared/statusComp";

const Step1 = ({
  programs,
  isFlexProgram = false,
  ingoProviderName = "",
  extraDetails = {},
  setIsGrantsAvailable = () => {}
}) => {
  const {
    peopleDomain,
    planetDomain,
    currentBrand,
    localeLanguage,
    currentSiteMeta
  } = useTheme();

  const [formStatus, setFormStatus] = useState("pending");
  const [programID, setProgramID] = useState("");
  const [programCode, setProgramCode] = useState("");
  const [programDuration, setProgramDuration] = useState("");
  const [programEnrolDate, setProgramEnrolDate] = useState("");
  const [programFundraising, setProgramFundraising] = useState("");
  const [programTitle, setProgramTitle] = useState("");
  const [programPopular, setProgramPopular] = useState(false);
  const [programCapacity, setProgramCapacity] = useState("");
  const [programType, setProgramType] = useState("");
  const [formType, setFormType] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(0);

  const { currency } = currentSiteMeta ?? {
    currency: process.env.GATSBY_PROJECT_CURRENCY ?? "gbp"
  };
  const currentSiteCurrency = currency?.toLowerCase();

  const formOptions = {
    defaultValues: {
      newsletter: false
    }
  };

  const form = useForm(formOptions);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue
  } = form;

  const customStyles = {
    option: (provided, state) => {
      return {
        ...provided,
        color: "current",
        fontWeight: state.isSelected ? "bold" : "current",
        background: "#fff",
        zIndex: 20,
        boxShadow: "none"
      };
    },
    control: (provided, state) => {
      return {
        ...provided,
        background: "transparent",
        padding: "4px",
        borderColor:
          Object.values(errors).length && !state.hasValue
            ? "rgb(220 38 38)"
            : "rgb(231 231 242)",
        borderRadius: "4px",
        boxShadow: 0,
        "&:hover": {
          boxShadow: "0"
        },
        "&:focus": {
          boxShadow: "0"
        }
      };
    },
    singleValue: (provided, state) => {
      const opacity = state.isDisabled ? 0.5 : 1;
      const transition = "opacity 300ms";
      const color = "white";
      const fontSize = "16px";
      const fontWeight = "bold";

      return { ...provided, opacity, transition, color, fontSize, fontWeight };
    },
    placeholder: (defaultStyles) => {
      return {
        ...defaultStyles,
        color: "#ffffff"
      };
    },
    dropdownIndicator: (base) => ({
      ...base,
      color: "#ccc",
      "&:hover": {
        color: "#ccc"
      },
      "&:focus": {
        color: "#ccc"
      }
    }),
    input: (base) => ({
      ...base,
      color: "#fff"
    }),
    menuList: (base) => ({
      ...base,
      backgroundColor: "#fff",
      paddingTop: "10px",
      paddingBottom: "10px",
      borderRadius: "4px"
    })
  };

  const spendingOptions = {
    gbp: [
      {
        value: "£1,500 - £3,000",
        label: "£1,500 - £3,000"
      },
      {
        value: "£3,001 - £5,000",
        label: "£3,001 - £5,000"
      },
      {
        value: "£5,000+",
        label: "£5,000+"
      },
      {
        value: "I don't have a way of paying for a GVI program right now",
        label: "I don't have a way of paying for a GVI program right now"
      }
    ],
    zar: [
      {
        value: "£1,500 - £3,000",
        label: "£1,500 - £3,000"
      },
      {
        value: "£3,001 - £5,000",
        label: "£3,001 - £5,000"
      },
      {
        value: "£5,000+",
        label: "£5,000+"
      },
      {
        value: "I don't have a way of paying for a GVI program right now",
        label: "I don't have a way of paying for a GVI program right now"
      }
    ],
    usd: [
      {
        value: "£1,500 - £3,000",
        label: "£1,500 - £3,000"
      },
      {
        value: "£3,001 - £5,000",
        label: "£3,001 - £5,000"
      },
      {
        value: "£5,000+",
        label: "£5,000+"
      },
      {
        value: "I don't have a way of paying for a GVI program right now",
        label: "I don't have a way of paying for a GVI program right now"
      }
    ],
    cad: [
      {
        value: "£1,500 - £3,000",
        label: "£1,500 - £3,000"
      },
      {
        value: "£3,001 - £5,000",
        label: "£3,001 - £5,000"
      },
      {
        value: "£5,000+",
        label: "£5,000+"
      },
      {
        value: "I don't have a way of paying for a GVI program right now",
        label: "I don't have a way of paying for a GVI program right now"
      }
    ],
    aud: [
      {
        value: "£1,500 - £3,000",
        label: "£1,500 - £3,000"
      },
      {
        value: "£3,001 - £5,000",
        label: "£3,001 - £5,000"
      },
      {
        value: "£5,000+",
        label: "£5,000+"
      },
      {
        value: "I don't have a way of paying for a GVI program right now",
        label: "I don't have a way of paying for a GVI program right now"
      }
    ],
    eur: [
      {
        value: "£1,500 - £3,000",
        label: "£1,500 - £3,000"
      },
      {
        value: "£3,001 - £5,000",
        label: "£3,001 - £5,000"
      },
      {
        value: "£5,000+",
        label: "£5,000+"
      },
      {
        value: "I don't have a way of paying for a GVI program right now",
        label: "I don't have a way of paying for a GVI program right now"
      }
    ]
  };

  const stageOptions = [
    {
      label: "Early stages, please send me info",
      value: "Early stages send info"
    },
    {
      label: "Early stages, I'd like to talk to someone at GVI",
      value: "Early stages talk"
    },
    {
      label: "I'm almost ready to book, but I have some questions",
      value: "Almost ready to book"
    },
    {
      label: "I'm ready to book",
      value: "Ready to book"
    }
  ];

  const showPricingExtra = false;

  const onSubmit = useCallback(
    async (data) => {
      try {
        window.scrollTo(0, 0);

        const formFields = {
          form_type: formType,
          parental_consent: "parental_consent",
          privacy_policy: "privacy_policy",
          program_id: programCode,
          ...data
        };

        // console.log("formFields", formFields);
        // return;

        setFormStatus("processing");

        const formProcessResult = await processForm({
          formdata: formFields,
          peopleDomain,
          planetDomain,
          currentBrand,
          localeLanguage
        });

        if (
          (formProcessResult?.response &&
            formProcessResult?.response[0] &&
            formProcessResult?.response[0].success) ||
          (formProcessResult?.response && formProcessResult?.response.success)
        ) {
          const firebaseId =
            formProcessResult["submitted-data"]?.firebaseId ||
            formProcessResult["submitted-data"]?.firestoreId ||
            formProcessResult["submitted-data"]?.firebaseID__c ||
            formProcessResult["submitted-data"][0]?.firebaseId ||
            formProcessResult["submitted-data"][0]?.firestoreId ||
            formProcessResult["submitted-data"][0]?.firebaseID__c ||
            "";

          const { "submitted-data": submittedData } = formProcessResult || {};

          const { lead_score } = submittedData || {};

          //set application data cookie here
          const forwardingStateData = {
            ...formFields,
            lead_id: formProcessResult?.response[0]
              ? formProcessResult?.response[0]?.id
              : formProcessResult?.response?.id ?? "",
            referring_form_type: formFields?.form_type,
            firebase_id: firebaseId,
            program_code: programCode,
            program_title: programTitle,
            program_type: programType,
            lead_score: lead_score || 0,
            program_id: programCode ?? formFields?.program_id //overwrite the program id with program code
          };

          // console.debug("formFields", formFields);
          // console.debug("forwardingStateData", forwardingStateData);

          // return;

          const redirectUrl = [
            "/thank-you-for-your-application/?_",
            new Date()?.getTime()
          ]?.join("");

          setFormStatus("processing");
          reset();

          //if gbp & people & in allowed country
          /**
           * The reason we are showing this on people only, is by design.
           * There was a specific userflow that was set out when pricing extra feature was introduced.
           * It therefore means that if we display the enrol bridge on all domains, the user will experience the calendly embed twice!
           * **/

          await navigate(redirectUrl, { state: forwardingStateData });
        }
      } catch (err) {
        console.error("Error requestForm :: ", err);
        setFormStatus("pending");
      }
    },
    [
      currentBrand,
      localeLanguage,
      peopleDomain,
      planetDomain,
      reset,
      formType,
      programCode,
      programTitle,
      programType
    ]
  );

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    const currentUrl = new URL(location);

    setProgramFundraising(currentUrl.searchParams.get("fundraising"));
    setProgramCode(
      extraDetails?.program_id ??
        extraDetails?.program_code ??
        currentUrl.searchParams.get("program") ??
        currentUrl.searchParams.get("program_id") ??
        currentUrl.searchParams.get("program_code")
    );
    setProgramEnrolDate(
      extraDetails?.departure_date ??
        currentUrl.searchParams.get("departure_date")
    );
    setProgramDuration(
      extraDetails?.duration ?? currentUrl.searchParams.get("duration")
    );
    setProgramID(
      extraDetails?.program_id ??
        currentUrl.searchParams.get("program") ??
        currentUrl.searchParams.get("program_id") ??
        currentUrl.searchParams.get("program_code")
    );

    setProgramTitle(currentUrl.searchParams.get("program_title") || "");

    //virtual and online programs - set shortcodes and use program code
    setFormType("application_form");
    if (currentUrl.search.includes("virtual_program")) {
      setFormType("gvivirtual_application_form");
    } else if (currentUrl.search.includes("online_program")) {
      setFormType("gvionline_application_form");
    } else if (currentUrl.search.includes("virtual_career")) {
      setFormType("gvivirtual_career_application_form");
    } else if (currentUrl.search.includes("virtual_uncc")) {
      setFormType("gvivirtual_uncc_application_form");
    } else if (currentUrl.search.includes("flex_program")) {
      setFormType("ingo_application_form");
    } else if (currentUrl.search.includes("journey_program")) {
      setFormType("journeys_application_form");
    }
  }, [extraDetails]);

  useEffect(() => {
    if (programID && programs) {
      // if the form type is not xyz_application_form

      if (!formType.includes("application_form")) {
        // console.log("programID", programID, formType, programs);
        return;
      }

      //get the program title & program code and set them
      const theProgram =
        programs
          .filter((program) =>
            /[a-zA-Z]/.test(programID)
              ? program?.acfProgramsMeta?.programCode === programID
              : parseInt(programID) === parseInt(program.databaseId)
          )
          .shift() ?? [];

      const programTypeList =
        theProgram?.acfProgramsMeta?.programTypes
          ?.flatMap((item) => item.slug)
          ?.join(",") ?? [];

      // console.log("theProgram", theProgram);
      // console.log("programType", programTypeList);
      setProgramTitle(theProgram?.title ?? programTitle ?? "");
      setProgramCode(
        theProgram?.acfProgramsMeta?.programCode ?? programID ?? ""
      );
      setProgramPopular(theProgram?.acfProgramsMeta?.programPopular ?? false);
      setProgramCapacity(theProgram?.acfProgramsMeta?.programCapacity ?? "");
      setProgramType(programTypeList ?? "");
    }
  }, [programID, formType, programs]);

  const termsUrl =
    programCode && programCode.toLowerCase().startsWith(bwMahlahlaProgramsTerm)
      ? "/bushwise-terms-and-conditions/"
      : "/terms-and-conditions";

  return (
    <div className="px-5 py-6 step-wrapper md:px-16">
      {formStatus === "pending" && (
        <FormProvider {...form}>
          <form
            id={"application-step-1"}
            className="w-full py-8 text-center md:text-left"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex justify-center md:justify-between items-center">
              <h2
                className={`pb-2 text-xl font-bold text-white md:font-semibold md:text-xlrg-sm ${
                  programTitle ? "md:pb-0" : "md:pb-5"
                }`}
              >
                Application form
              </h2>

              <div className="hidden sm:block">
                <ProgramRating
                  outerContainerClass={"items-end justify-end"}
                  ratingIconClass={"fill-white h-5"}
                  ratingLogoClass={"fill-white h-5"}
                />
              </div>
            </div>

            {programTitle && (
              <h3
                className={`${
                  showPricingExtra ? "text-white" : "text-gray-standard"
                } pt-2 pb-5 ${
                  isFlexProgram ? "md:pb-8" : "md:pb-2"
                } text-[19px] md:text-xl font-light  planet:font-light planet:text-white sm:text-white font-alice planet:font-body`}
              >
                You are applying for{" "}
                <span className="font-normal text-white planet:font-semibold planet:font-body font-alice">
                  {programTitle}
                </span>
              </h3>
            )}

            {!isFlexProgram && programPopular && programCapacity && (
              <ProgramCapacity
                programCapacityText={programCapacity}
                outerContainerClass={"justify-center md:justify-start pb-4"}
                headerTitleClass={"md:!text-white"}
                headerClass={"h6 !text-white"}
              />
            )}

            <div className="block sm:hidden">
              <ProgramRating
                outerContainerClass="items-center justify-center pb-4"
                darkContainerClass="flex gap-2 items-center"
                darkRatingIconClass="block fill-white"
                ratingIconClass="fill-white h-5"
                ratingLogoClass="fill-white h-5"
              />
            </div>

            <div className="flex flex-wrap justify-between py-2 form-wrapper md:flex-row relative">
              <label className="w-full md:semi-w-2 relative">
                <input
                  {...register("first_name", {
                    required: true,
                    pattern: /^[^\s].*/
                  })}
                  placeholder="First Name*"
                  className={`w-full py-2 pl-[17.5px] pr-16 md:pr-11 mb-3 text-base text-white bg-transparent border focus:outline-0 placeholder:text-base placeholder:font-bold placeholder:bg-transparent placeholder:text-white border-gray-testimonial ${
                    errors.first_name ? " !border-red-600 " : ""
                  }`}
                />
                <div
                  className={`tooltip tooltip-left w-1/6 md:w-5 !absolute py-3 mx-0 md:mx-2 right-0 top-0 md:right-5 before:py-3 before:rounded-xxs before:bg-white before:planet:bg-gray-standard before:text-gray-standard before:planet:text-white tooltip-${tooltipOpen}`}
                  data-tip="This is how we will refer to you."
                >
                  <InfoIcon
                    onClick={() => setTooltipOpen(1 - tooltipOpen)}
                    className="fill-white planet:fill-white stroke-transparent group-hover:sm:fill-white ml-2 z-10"
                  />
                </div>{" "}
              </label>
              {/* register an input */}
              <input
                {...register("last_name", {
                  required: true,
                  pattern: /^[^\s].*/
                })}
                placeholder="Last Name*"
                className={`w-full py-2 pl-[17.5px] mb-3 text-base text-white bg-transparent border focus:outline-0 placeholder:text-base placeholder:font-bold placeholder:text-white border-gray-testimonial md:semi-w-2 ${
                  errors.last_name ? " !border-red-600 " : ""
                }`}
              />
              <input
                {...register("phone_number", {
                  pattern: /\d+/,
                  required: true
                })}
                placeholder="Phone*"
                type="number"
                className={`w-full py-2 pl-[17.5px] mb-3 text-base text-white bg-transparent border focus:outline-0 placeholder:text-base placeholder:font-bold placeholder:text-white border-gray-testimonial md:semi-w-2  ${
                  errors.phone_number ? " !border-red-600 " : ""
                }`}
              />
              <input
                {...register("email_address", {
                  required: true,
                  pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                })}
                placeholder="Email*"
                className={` w-full py-2 pl-[17.5px] mb-3 text-base text-white bg-transparent border focus:outline-0 placeholder:text-base placeholder:font-bold placeholder:text-white border-gray-testimonial md:semi-w-2  ${
                  errors.email_address ? " !border-red-600 " : ""
                }`}
                onInput={(e) =>
                  (e.target.value = ("" + e.target.value).toLowerCase())
                }
              />

              {showPricingExtra && (
                <div
                  className={`planet:hidden w-full justify-between pb-5 ${
                    showPricingExtra ? "flex flex-col sm:flex-row" : "hidden"
                  }`}
                >
                  <FormInputSelect
                    name="program_spend"
                    options={spendingOptions[currentSiteCurrency]}
                    handleChange={""}
                    setValue={setValue}
                    placeholder="How much would you spend on a GVI program?*"
                    className={`w-full h-full col-span-full sm:col-span-2 mb-3 bg-transaparent select-in-step select-menu-app-form md:mb-0 text-left md:semi-w-2 text-gray-lighter  ${
                      errors.program_spend ? " !border-red-600 " : ""
                    }`}
                    styles={customStyles}
                    rules={{ required: true }}
                    components={{
                      IndicatorSeparator: () => null
                    }}
                  />

                  <FormInputSelect
                    name="decision_stage"
                    options={stageOptions}
                    handleChange={""}
                    styles={customStyles}
                    setValue={setValue}
                    placeholder="What stage are you at in thinking about a program?*"
                    className={`w-full h-full col-span-full sm:col-span-2 mb-3 bg-transparent select-in-step select-menu-app-form md:mb-0 text-left md:semi-w-2 text-gray-lighter ${
                      errors.decision_stage ? " !border-red-600 " : ""
                    }`}
                    rules={{ required: true }}
                    components={{
                      IndicatorSeparator: () => null
                    }}
                  />
                </div>
              )}

              {!showPricingExtra && (
                <div
                  className={`planet:block w-full ${
                    showPricingExtra ? "hidden" : ""
                  }`}
                >
                  <label htmlFor="motiv" className="pb-2 text-white">
                    (Optional) Why do you want to apply for this program?
                  </label>
                  <textarea
                    {...register("why_want_to_apply")}
                    id="motiv"
                    cols="30"
                    rows="5"
                    className="w-full h-16 py-2 pl-[17.5px] mb-3 text-base text-white bg-transparent border focus:outline-0 placeholder:text-white border-gray-testimonial"
                  />
                </div>
              )}

              <div className="flex flex-col gap-3">
                <p className="text-left text-xs text-white">
                  By clicking &quot;Apply&quot; below, I have read and accepted
                  GVI&apos;s{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    className="text-white border-b border-white planet:sm:border-white font-semi-bold planet:border-b-white hover:border-b-yellow-standard planet:hover:border-green-standard hover:text-yellow-standard planet:hover:!text-green-standard active:border-b-yellow-standard planet:active:border-green-standard active:text-yellow-standard planet:active:!text-green-standard"
                  >
                    Privacy Policy
                  </a>
                  . In opening hours, you will be contacted by us within a few
                  minutes.
                </p>

                {currentSiteCurrency === "usd" && (
                  <label
                    htmlFor="sms_consent"
                    className={`${currentSiteCurrency} font-semi-bold h8 pb-0 flex !text-white custom-checkbox`}
                  >
                    <input
                      type="checkbox"
                      className="absolute"
                      value={"sms_consent"}
                      id="sms_consent"
                      {...register("sms_consent")}
                    />
                    <span className="checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center" />
                    I would like to receive texts from GVI, including program
                    info and easy appointment booking links.
                  </label>
                )}
                <label
                  htmlFor="newsletter"
                  className="font-semi-bold h8 pb-0 flex !text-white custom-checkbox"
                >
                  <input
                    type="checkbox"
                    {...register("newsletter")}
                    id="newsletter"
                    className="absolute"
                    value={"newsletter_signup"}
                  />
                  <span className="checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center" />
                  I would like to sign up to the GVI newsletter to receive
                  updates, news and special offers.
                </label>
                <label
                  htmlFor="tc_agree"
                  className={`font-semi-bold h8 pb-2 flex !text-white custom-checkbox ${
                    errors.tc_agree ? " font-bold" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    {...register("tc_agree", { required: true })}
                    id="tc_agree"
                    className="absolute"
                    value={"tc_agree"}
                  />
                  <span
                    className={`checkmark w-7.5 sm:w-4.5 h-7.5 sm:h-4.5 bg-transparent border border-gray-testimonial rounded-xxs flex shrink-0 justify-center items-center  ${
                      errors.tc_agree ? " !border-red-600 " : ""
                    }`}
                  />
                  I have read, understand and accept{" "}
                  {programCode &&
                  programCode.toLowerCase().startsWith(bwMahlahlaProgramsTerm)
                    ? "Bushwise's"
                    : "GVI's"}{" "}
                  <a
                    href={termsUrl}
                    rel="noreferrer"
                    target="_blank"
                    className="underline"
                  >
                    Terms and Conditions
                  </a>{" "}
                  which includes details on our policy for cancellations,
                  refunds and{" "}
                  <a
                    href="/payment-plans"
                    rel="noreferrer"
                    target="_blank"
                    className="underline"
                  >
                    payment plans.
                  </a>{" "}
                  *
                </label>
              </div>
              <div className="hidden hidden-fields">
                {programDuration && (
                  <input
                    type="hidden"
                    defaultValue={programDuration}
                    {...register("duration")}
                  />
                )}

                {programEnrolDate && (
                  <input
                    type="hidden"
                    defaultValue={programEnrolDate}
                    {...register("departure_date")}
                  />
                )}

                {programFundraising && (
                  <input
                    type="hidden"
                    defaultValue={programFundraising}
                    {...register("fundraising")}
                  />
                )}

                {formType && (
                  <input
                    type="hidden"
                    defaultValue={formType}
                    {...register("form_type")}
                  />
                )}

                {ingoProviderName && ingoProviderName.length > 1 && (
                  <input
                    type="hidden"
                    defaultValue={ingoProviderName}
                    {...register("flex_name")}
                  />
                )}

                {isFlexProgram && (
                  <input
                    type="hidden"
                    defaultValue={isFlexProgram}
                    {...register("flex_program")}
                  />
                )}
              </div>
              <div className="flex justify-center w-full pt-8">
                <CtaButton
                  ctaLabel={`${showPricingExtra ? "Next" : "Apply"}`}
                  preset={"primary"}
                  labelClass="text-base leading-genericCta pr-2 font-bold"
                  extraButtonStyle={"w-full sm:w-56 min-w-56"}
                  showAsButton={true}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      )}

      {formStatus === "processing" && (
        <>
          <div className="w-3/4 py-12 m-auto ">
            <StatusComp
              expText={"Just a moment..."}
              statusText={`We’re making sure we have everything we need before connecting you with one of our team members!`}
              status={formStatus}
            />
          </div>
        </>
      )}
    </div>
  );
};
export default Step1;
